import { createSelector } from "@reduxjs/toolkit";
import type { PostMultiplacerDrofusInterfaceRoomChangesApiResponse } from "../../openapi";
import { openapi } from "../../openapi";
import { IRootState } from "../app/store";
import type { DrofusForekomstDetailsRef } from "../DrofusForekomster/DrofusForekomstDetailsModal";
import { selectSelectedSpaceNumber } from "../sharedSelectors";
import {
  selectDrofusChangeEventMatchesActiveFilters,
  selectDrofusChangesApiArg,
} from "./drofusChangesSelectors";
import { DROFUS_CHANGE_TYPE_OPTIONS } from "../components/DrofusChangeTypeFilter";

type DrofusChangeEvent = PostMultiplacerDrofusInterfaceRoomChangesApiResponse["items"][number];

export interface IRoomDrofusChangeRow {
  id: string;
  changeType: DrofusChangeEvent["changeType"];
  disciplines: string[];
  title: string;
  fieldLabel: string;
  oldValue: string;
  newValue: string;
  runStartedAt: string;
  color: string;
  occurrenceRef: DrofusForekomstDetailsRef | null;
}

export interface IRoomDrofusChangeSummary {
  added: number;
  removed: number;
  updated: number;
  total: number;
}

const formatValue = (value: unknown): string => {
  if (value == null || value === "") return "-";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return JSON.stringify(value);
};

const getChangeTypeColor = (changeType: DrofusChangeEvent["changeType"]): string =>
  DROFUS_CHANGE_TYPE_OPTIONS.find((option) => option.value === changeType)
    ?.activeBackground ?? "#868e96";

const getOccurrenceRef = (event: DrofusChangeEvent): DrofusForekomstDetailsRef | null => {
  if (event.entityType !== "occurrence") return null;

  const occurrenceId =
    typeof event.entityId === "number"
      ? event.entityId
      : Number.parseInt(String(event.entityId).replace(/^occurrence:/, ""), 10);
  if (!Number.isFinite(occurrenceId)) return null;

  return {
    projectNo: event.projectNo,
    occurrenceId,
    runId: event.changeType === "removed" ? event.baseRunId : event.runId,
  };
};

export const selectRoomDrofusChangesQueryResult = createSelector(
  [selectDrofusChangesApiArg, (state: IRootState) => state],
  (apiArg, state) => {
    if (!apiArg) return undefined;
    return openapi.endpoints.postMultiplacerDrofusInterfaceRoomChanges.select(apiArg)(state);
  }
);

export const selectRoomDrofusChangeEvents = createSelector(
  [
    selectSelectedSpaceNumber,
    selectRoomDrofusChangesQueryResult,
    selectDrofusChangeEventMatchesActiveFilters,
  ],
  (selectedRoomNumber, queryResult, eventMatchesActiveFilters): DrofusChangeEvent[] => {
    if (!selectedRoomNumber) return [];

    return (queryResult?.data?.items ?? []).filter((item) => {
      if (!eventMatchesActiveFilters(item)) return false;
      return item.context.room?.functionNo === selectedRoomNumber;
    });
  }
);

export const selectRoomDrofusChangeSummary = createSelector(
  [selectRoomDrofusChangeEvents],
  (events): IRoomDrofusChangeSummary =>
    events.reduce(
      (acc, event) => {
        acc[event.changeType] += 1;
        acc.total += 1;
        return acc;
      },
      { added: 0, removed: 0, updated: 0, total: 0 }
    )
);

export const selectRoomDrofusChangeRows = createSelector(
  [selectRoomDrofusChangeEvents],
  (events): IRoomDrofusChangeRow[] =>
    events
      .map((event) => {
        const articleName = event.context.article?.name?.trim();
        const compCode = event.context.article?.compCode?.trim();
        const fallbackTitle = event.entityType === "room" ? "Rom" : "Forekomst";
        const titleParts = [compCode, articleName].filter(Boolean);

        return {
          id: `${event.runId}:${event.stableKey}:${event.changeKey}`,
          changeType: event.changeType,
          disciplines: event.affectedDisciplines,
          title: titleParts.length > 0 ? titleParts.join(" - ") : fallbackTitle,
          fieldLabel: event.displayName ?? event.normalizedPath ?? event.changeKey,
          oldValue: formatValue(event.oldValue),
          newValue: formatValue(event.newValue),
          runStartedAt: event.runStartedAt,
          color: getChangeTypeColor(event.changeType),
          occurrenceRef: getOccurrenceRef(event),
        };
      })
      .sort((a, b) => b.runStartedAt.localeCompare(a.runStartedAt))
);
